<script lang="ts">
  import Reviews from './reviews/reviews.svelte';
  import SplitLines from '$components/split-lines.svelte';
  import ProductDetail from '$components/blocks/product-detail.svelte';

  import { splitTitleAndCategory } from '$lib/utils/shopify/splitTitleAndCategory';
  import { isCmsStory } from '$lib/utils/cms';

  import type { GetProductQuery } from '$gql/storefront/operations';
  import type {
    ProductDetailStoryblok,
    ProductInfoCardStoryblok,
    SyncedProductDetailStoryblok
  } from '$types/bloks';
  import { cn } from '$lib/utils';
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
  import { page } from '$app/stores';
  import { DEFAULT_TITLE } from '$lib/constants';
  import { onMount } from 'svelte';
  import ProductInfoCard from './product-info-card.svelte';

  export let description: string | undefined = undefined;
  export let product: NonNullable<GetProductQuery['product']>;
  export let details: (ProductDetailStoryblok | SyncedProductDetailStoryblok)[] | undefined;
  export let additionalInformation:
    | (ProductDetailStoryblok | SyncedProductDetailStoryblok)[]
    | undefined;
  export let productInfoCard: ProductInfoCardStoryblok[] | undefined;
  export let selectedOptions: Record<string, string>;
  export let hideProductDetailDrawersMobile: boolean | undefined = undefined;
  export let hideProductDetailDrawersDesktop: boolean | undefined = undefined;
  export let hideReviewsInMobile: boolean | undefined = undefined;
  export let hideReviewsInDesktop: boolean | undefined = undefined;

  let productVariantName = '';

  const { title, category } = splitTitleAndCategory({
    title: product.title,
    titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
  });
  const currency = product.priceRange?.minVariantPrice?.currencyCode;
  const price = +product.priceRange?.minVariantPrice?.amount;

  onMount(() => {
    const selectedVariant = product.variants.nodes.find(
      (variant) => variant.title === Object.values(selectedOptions).join(' / ')
    );

    if (!selectedVariant?.title) {
      const availableVariant = product.variants.nodes.find((item) => item.availableForSale);
      productVariantName =
        availableVariant?.title && availableVariant?.title !== DEFAULT_TITLE
          ? availableVariant.title
          : product.title;
    }
  });

  const onInteract = (block: ProductDetailStoryblok | SyncedProductDetailStoryblok) => {
    const title = (() => {
      if (block.component === 'product-detail') {
        return block.title;
      } else {
        if (isCmsStory(block.synced_block)) return block.synced_block.content.body[0].title;
        else return '';
      }
    })();

    analyticsEvent({
      event: AnalyticsEvents.OPEN_PRODUCT_DETAIL,
      title,
      items: [
        {
          item_id: product.handle,
          item_name: product.title,
          item_category: product.productType,
          item_variant: productVariantName
        }
      ]
    });
  };
</script>

{#if description}
  <SplitLines class="flex flex-col gap-3 text-md-body mb-8 mt-4" text={description} />
{/if}
<div class={cn('flex flex-col gap-2', !description && 'mt-2')}>
  {#if productInfoCard}
    <ProductInfoCard {productInfoCard} />
  {/if}
  <Reviews
    product={{
      id: product.id,
      title,
      category,
      price,
      currency,
      variant: productVariantName,
      handle: product.handle,
      productType: product.productType,
      image: product.images.nodes?.[0]?.url || ''
    }}
    class={cn(hideReviewsInMobile && 'hidden lg:block', hideReviewsInDesktop && 'lg:hidden')}
  />

  {#if !!details?.length}
    {#each details as block}
      {#if block.component === 'product-detail'}
        <ProductDetail
          {block}
          onInteract={() => onInteract(block)}
          class={cn(
            hideProductDetailDrawersMobile && 'hidden lg:block',
            hideProductDetailDrawersDesktop && 'lg:hidden'
          )}
        />
      {:else if block.component === 'synced-product-detail'}
        {@const syncedBlock = block.synced_block}
        {#if isCmsStory(syncedBlock)}
          <ProductDetail
            block={syncedBlock.content.body[0]}
            onInteract={() => onInteract(block)}
            class={cn(
              hideProductDetailDrawersMobile && 'hidden lg:block',
              hideProductDetailDrawersDesktop && 'lg:hidden'
            )}
          />
        {/if}
      {/if}
    {/each}
  {/if}

  {#if !!additionalInformation?.length}
    {#each additionalInformation as block}
      {#if block.component === 'product-detail'}
        <ProductDetail
          {block}
          onInteract={() => onInteract(block)}
          class={cn(
            hideProductDetailDrawersMobile && 'hidden lg:block',
            hideProductDetailDrawersDesktop && 'lg:hidden'
          )}
        />
      {:else if block.component === 'synced-product-detail'}
        {@const syncedBlock = block.synced_block}
        {#if isCmsStory(syncedBlock)}
          <ProductDetail
            block={syncedBlock.content.body[0]}
            onInteract={() => onInteract(block)}
            class={cn(
              hideProductDetailDrawersMobile && 'hidden lg:block',
              hideProductDetailDrawersDesktop && 'lg:hidden'
            )}
          />
        {/if}
      {/if}
    {/each}
  {/if}
</div>
